/**
 * Referans motoru: metin içi atıf ↔ kaynakça çapraz kontrolü.
 *
 * 1. Kaynakça bölümü başlıklardan tespit edilir.
 * 2. Metin içi atıflar (Yılmaz, 2019; (Smith & Jones, 2020); Yılmaz vd., 2021)
 *    ve kaynakça girdileri ayrıştırılır.
 * 3. Yazar soyadı + yıl anahtarıyla eşleştirilir; yakın ama birebir olmayan
 *    eşleşmeler "yıl/yazar uyuşmazlığı" olarak işaretlenir.
 *
 * Tüm kararlar confidence taşır: kaynakça tespit edilemezse sonuç "kontrol
 * edilemedi" olur, sahte hata üretilmez.
 */

import type {
	Citation,
	CitationMatch,
	Confidence,
	Issue,
	ReferenceEntry,
	ReferenceResult
} from '../types';
import type { DocumentIndex } from '../text/document';
import { excerpt, foldTurkish } from '../text/normalize';

const YEAR_ALT = String.raw`(?:\d{4}[a-z]?|n\.?\s?d\.?|baskıda|in\s+press)`;

const CITATION_PATTERNS: { re: RegExp; narrative: boolean }[] = [
	// (Yılmaz, 2019; Smith & Jones, 2020a; Öztürk vd., 2021)
	{
		re: new RegExp(
			String.raw`\(\s*((?:[A-ZÇĞİÖŞÜ][\p{L}'’\-]+(?:\s*(?:&|ve|and)\s*[A-ZÇĞİÖŞÜ][\p{L}'’\-]+)?(?:\s+(?:vd\.?|et\s+al\.?))?\s*,?\s*${YEAR_ALT}\s*(?:,\s*(?:s\.|p\.|pp\.)\s*[\d–-]+)?)(?:\s*;\s*[^()]{3,120})*)\s*\)`,
			'gu'
		),
		narrative: false
	},
	// Yılmaz (2019) / Smith and Jones (2020) / Öztürk vd. (2021)
	{
		re: new RegExp(
			String.raw`(?<![\p{L}])([A-ZÇĞİÖŞÜ][\p{L}'’\-]+(?:\s*(?:&|ve|and)\s*[A-ZÇĞİÖŞÜ][\p{L}'’\-]+)?(?:\s+(?:vd\.?|et\s+al\.?))?)\s*\(\s*(${YEAR_ALT})\s*(?:,\s*(?:s\.|p\.|pp\.)\s*[\d–-]+)?\s*\)`,
			'gu'
		),
		narrative: true
	}
];

const SECTION_RE =
	/^\s*(?:\d+[.)]?\s*)?(kaynakça|kaynaklar|referanslar|bibliyografya|references|bibliography|works\s+cited)\s*[:.]?\s*$/iu;
const SECTION_INLINE_RE =
	/(?:^|\n)\s*(?:\d+[.)]?\s*)?(kaynakça|kaynaklar|referanslar|bibliyografya|references|bibliography|works\s+cited)\s*[:.]?\s*(?:\n|$)/giu;

export interface ReferencesEngineOutput {
	result: ReferenceResult;
	issues: Issue[];
}

export function runReferenceEngine(index: DocumentIndex): ReferencesEngineOutput {
	const text = index.text;
	const issues: Issue[] = [];

	const section = findReferencesSection(text);
	const citationText = section ? text.slice(0, section.start) : text;
	const citations = extractCitations(citationText).map((c) => ({
		...c,
		location:
			c.location.charStart !== undefined
				? index.locationAt(c.location.charStart, c.location.charEnd ?? c.location.charStart)
				: c.location
	}));
	let references: ReferenceEntry[] = [];
	let sectionDetected = false;

	if (section) {
		sectionDetected = true;
		references = extractReferenceEntries(text, section.start, section.end);
	}

	// Reference gate: kaynakça bölümü/girdileri güvenilir değilse tek uyarı üret,
	// atıf başına "missing" kararı verme.
	const gateReason = !sectionDetected
		? 'section'
		: references.length === 0 && citations.length > 0
			? 'empty'
			: citations.length === 0 && references.length > 0
				? 'unmatched'
				: null;

	if (gateReason) {
		issues.push({
			id: 'ref-gate',
			code: 'cite.no_reference_section',
			category: 'citations',
			severity: 'warning',
			title: 'Atıf kontrolü yapılamadı',
			detail:
				gateReason === 'section'
					? `${citations.length} metin içi atıf var ancak kaynakça bölümü otomatik olarak algılanamadı.`
					: gateReason === 'empty'
						? 'Kaynakça başlığı bulundu ancak kaynak girişleri okunamadı.'
						: 'Kaynakça bulundu ancak metin içi atıflar bu biçimde eşleştirilemedi (numaralı atıf olabilir).',
			suggestion:
				'Kaynakça başlığını ve giriş biçimini kontrol edin; gerekirse atıfları manuel doğrulayın.',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { gate: gateReason, citations: citations.length, references: references.length }
		});
		return {
			result: {
				citations,
				references,
				matches: [],
				orphans: [],
				sectionDetected,
				summary: {
					citations: citations.length,
					references: references.length,
					matched: 0,
					missing: 0,
					orphans: 0,
					mismatched: 0
				},
				score: 100
			},
			issues
		};
	}

	const { matches, orphans } = matchCitations(citations, references);

	/* Issue üretimi -------------------------------------------------------- */

	for (const m of matches) {
		if (m.status === 'missing') {
			const candidates = m.candidates
				.slice(0, 3)
				.map((c) => c.key ?? `${c.authors[0] ?? '?'} ${c.year ?? '?'}`)
				.join(', ');
			issues.push({
				id: `ref-missing-${issues.length}`,
				code: 'cite.missing_in_references',
				category: 'citations',
				severity: 'critical',
				title: 'Metin içi atıf kaynakçada yok',
				detail:
					`"${m.citation.raw}" atfına kaynakçada karşılık gelen giriş bulunamadı.` +
					(candidates ? ` Benzer girdiler: ${candidates}.` : ''),
				quote: m.citation.location.line ?? m.citation.raw,
				suggestion:
					'Kaynakçaya eksik girdiyi ekleyin; yıl veya yazar adı yazımını kontrol edin.',
				location: m.citation.location,
				confidence: m.citation.confidence === 'low' ? 'low' : 'high',
				decisive: m.citation.confidence !== 'low',
				citationKey: `${m.citation.authors[0] ?? ''}-${m.citation.year ?? ''}`,
				meta: {
					citation: m.citation.raw,
					candidates: m.candidates.slice(0, 3).map((c) => c.key ?? `${c.authors[0] ?? '?'} ${c.year ?? '?'}`)
				}
			});
		} else if (m.status === 'year-mismatch') {
			issues.push({
				id: `ref-year-${issues.length}`,
				code: 'cite.year_mismatch',
				category: 'citations',
				severity: 'warning',
				title: 'Atıf yılı kaynakçayla uyuşmuyor',
				detail: `"${m.citation.raw}" atfında ${m.citation.year} yılı var; kaynakçadaki eşleşen giriş ${m.matched?.year ?? 'farklı'} yılını gösteriyor.`,
				quote: m.citation.location.line ?? m.citation.raw,
				suggestion: 'Atıf yılını kaynakçadaki yayın yılıyla eşleştirin.',
				location: m.citation.location,
				confidence: 'high',
				decisive: true,
				meta: { citation: m.citation.raw, year: m.citation.year, matchedYear: m.matched?.year }
			});
		} else if (m.status === 'author-mismatch') {
			issues.push({
				id: `ref-author-${issues.length}`,
				code: 'cite.author_mismatch',
				category: 'citations',
				severity: 'warning',
				title: 'Yazar adı kaynakçayla uyuşmuyor',
				detail: `"${m.citation.raw}" atfındaki yazar adı kaynakçada "${m.matched?.authors.join(', ')}" olarak geçiyor.`,
				quote: m.citation.location.line ?? m.citation.raw,
				suggestion: 'Yazar soyadının yazımını (Türkçe karakterler dahil) kontrol edin.',
				location: m.citation.location,
				confidence: 'medium',
				decisive: true,
				meta: { citation: m.citation.raw, authors: m.matched?.authors.join(', ') }
			});
		}
	}

	for (const orphan of orphans) {
		issues.push({
			id: `ref-orphan-${issues.length}`,
			code: 'cite.orphan_reference',
			category: 'citations',
			severity: 'warning',
			title: 'Kaynakçada olup metinde atıf verilmeyen kaynak',
			detail: `"${excerpt(orphan.raw, 140)}" kaynakçada var ancak metin içinde bu kaynağa atıf bulunamadı.`,
			quote: excerpt(orphan.raw, 180),
			suggestion: 'Kaynağı metinde ilgili yerde atıflayın veya kaynakçadan çıkarın.',
			location: orphan.location,
			confidence: orphan.confidence,
			decisive: orphan.confidence !== 'low',
			meta: { raw: orphan.raw }
		});
	}

	/* Skor ---------------------------------------------------------------- */

	const matched = matches.filter((m) => m.status === 'matched').length;
	const missing = matches.filter((m) => m.status === 'missing').length;
	const mismatched = matches.filter(
		(m) => m.status === 'year-mismatch' || m.status === 'author-mismatch'
	).length;

	let score = 100;
	if (matches.length > 0) {
		score -= (missing / matches.length) * 60;
		score -= (mismatched / matches.length) * 25;
	}
	if (references.length > 0) {
		score -= Math.min(30, (orphans.length / references.length) * 45);
	}
	if (!sectionDetected && citations.length > 0) score = Math.min(score, 35);
	if (citations.length === 0 && references.length === 0) score = 100;

	return {
		result: {
			citations,
			references,
			matches,
			orphans,
			sectionDetected,
			summary: {
				citations: citations.length,
				references: references.length,
				matched,
				missing,
				orphans: orphans.length,
				mismatched
			},
			score: clamp(Math.round(score))
		},
		issues
	};
}

/* -------------------------------------------------------------------------- */
/* Kaynakça bölümü                                                             */
/* -------------------------------------------------------------------------- */

export function findReferencesSection(text: string): { start: number; end: number } | null {
	// Son eşleşmeyi tercih et: gövdede geçen "references" kelimesi bölüm değildir.
	const re = new RegExp(SECTION_INLINE_RE.source, 'giu');
	let best: { start: number; end: number } | null = null;
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const start = m.index + (m[0].startsWith('\n') ? 1 : 0);
		const end = m.index + m[0].length;
		// Başlık satırı kısa olmalı (yalnızca başlık)
		const line = text.slice(start, end).trim();
		if (line.length > 40) continue;
		best = { start: end, end: text.length };
	}
	// Ek/ekler kaynakçadan sonra gelebilir; onları kes
	if (best) {
		const tail = text.slice(best.start);
		const appendix = /(?:^|\n)\s*(?:ek\s*[-:]?\s*[a-z]|appendix\s*[a-z]?|ekler)\s*(?:\n|$)/i.exec(
			tail
		);
		if (appendix) best.end = best.start + appendix.index;
	}
	return best;
}

/* -------------------------------------------------------------------------- */
/* Atıf çıkarma                                                                */
/* -------------------------------------------------------------------------- */

export function extractCitations(text: string): Citation[] {
	const out: Citation[] = [];
	const seen = new Set<string>();

	for (const { re, narrative } of CITATION_PATTERNS) {
		const local = new RegExp(re.source, re.flags);
		let m: RegExpExecArray | null;
		while ((m = local.exec(text))) {
			const raw = m[0];
			// İçerik: parantez içi ya da yazar+yıl
			const inner = narrative ? `${m[1]} ${m[2]}` : m[1];
			if (!inner || inner.length > 400) continue;

			// İstatistik/yıl aralığı yanlış pozitiflerini ele
			if (/\bp\s*[<>=,;]/.test(inner)) continue;
			if (/^(?:bkz|see|cf|aktaran|alıntılayan)\b/i.test(inner)) continue;

			const segments = splitCitationSegments(inner);
			for (const seg of segments) {
				const parsed = parseCitationSegment(seg, narrative);
				if (!parsed) continue;
				const key = `${foldTurkish(parsed.authors.join('&')).toLowerCase()}|${parsed.year ?? ''}`;
				if (seen.has(key)) continue;
				seen.add(key);
				out.push({
					id: `cit-${out.length + 1}`,
					raw: seg.trim(),
					authors: parsed.authors,
					year: parsed.year,
					etAl: parsed.etAl,
					narrative,
					location: { charStart: m.index, charEnd: m.index + raw.length },
					confidence: parsed.confidence
				});
			}
		}
	}

	// Numaralı atıflar: [1], [2,3], [4–6]
	const numericRe = /\[(\d{1,3}(?:\s*[-,–]\s*\d{1,3})*)\]/g;
	let nm: RegExpExecArray | null;
	while ((nm = numericRe.exec(text))) {
		const parts = nm[1].split(',').flatMap((part) => {
			const range = part.split(/[-–]/).map((v) => Number(v.trim()));
			if (range.length === 2 && Number.isFinite(range[0]) && Number.isFinite(range[1])) {
				const out: number[] = [];
				for (let n = range[0]; n <= range[1] && n - range[0] < 100; n++) out.push(n);
				return out;
			}
			return [Number(part.trim())];
		});
		for (const n of parts) {
			if (!Number.isFinite(n) || n <= 0) continue;
			const key = `num|${n}`;
			if (seen.has(key)) continue;
			seen.add(key);
			out.push({
				id: `cit-${out.length + 1}`,
				raw: `[${n}]`,
				authors: [],
				number: n,
				etAl: false,
				narrative: false,
				location: { charStart: nm.index, charEnd: nm.index + nm[0].length },
				confidence: 'high'
			});
		}
	}

	// Konumları belge indeksine göre çöz
	return out;
}

function splitCitationSegments(inner: string): string[] {
	// "(Yılmaz, 2019; Smith & Jones, 2020)" → ["Yılmaz, 2019", "Smith & Jones, 2020"]
	if (inner.includes(';')) return inner.split(';').map((s) => s.trim()).filter(Boolean);
	return [inner.trim()];
}

interface ParsedCitation {
	authors: string[];
	year?: number;
	etAl: boolean;
	confidence: Confidence;
}

function parseCitationSegment(seg: string, narrative: boolean): ParsedCitation | null {
	const cleaned = seg.replace(/\s+/g, ' ').trim();
	if (cleaned.length === 0) return null;

	// Yıl: metin içinde herhangi bir yerde (sayfa numarası sonda olabilir)
	const ym = new RegExp(String.raw`(${YEAR_ALT})`).exec(cleaned);
	const year = ym && /^\d{4}/.test(ym[1]) ? Number(ym[1].slice(0, 4)) : undefined;

	// Yazar kısmı: yıldan önce
	let authorPart = ym ? cleaned.slice(0, ym.index) : cleaned;
	if (narrative) authorPart = authorPart.replace(/\(\s*$/, '');
	authorPart = authorPart.replace(/[,\s]+$/, '').trim();
	if (authorPart.length === 0) return null;

	const etAl = /\b(?:et\s+al\.?|vd\.?)\b/i.test(authorPart);
	authorPart = authorPart.replace(/\s+(?:et\s+al\.?|vd\.?)\s*$/i, '').trim();
	authorPart = authorPart.replace(/\s*(?:&|ve|and)\s*/gi, ', ');

	const authors = authorPart
		.split(',')
		.map((a) => a.trim())
		.map((a) => normalizeAuthorName(a))
		.filter((a) => a.length > 0 && a.length < 60)
		// Baş harf parçalarını ayıkla: "J." → at
		.filter((a) => !/^[A-ZÇĞİÖŞÜ]\.?$/.test(a));

	if (authors.length === 0) return null;

	// Çok sayıda yazar listesi (3+) → et al. kabul
	let confidence: Confidence = 'high';
	if (!year) confidence = 'medium';
	if (etAl) confidence = 'medium';
	if (/^(?:diğerleri|others)$/i.test(authors[authors.length - 1])) confidence = 'low';

	return { authors, year, etAl, confidence };
}

function normalizeAuthorName(a: string): string {
	let s = a.trim();
	// "Yılmaz, A." → "Yılmaz"
	s = s.replace(/\s+[A-ZÇĞİÖŞÜ]\.(?:\s*[A-ZÇĞİÖŞÜ]\.)*$/, '');
	s = s.replace(/^[A-ZÇĞİÖŞÜ]\.\s*/, '');
	// "J. Smith" → "Smith"
	s = s.replace(/^(?:[A-ZÇĞİÖŞÜ]\.\s*)+/, '');
	s = s.replace(/[.*+?^${}()|[\]\\]/g, '');
	return s.trim();
}

/* -------------------------------------------------------------------------- */
/* Kaynakça girdileri                                                          */
/* -------------------------------------------------------------------------- */

export function extractReferenceEntries(
	text: string,
	start: number,
	end: number
): ReferenceEntry[] {
	const block = text.slice(start, end);
	if (block.trim().length < 20) return [];

	const numbered = extractNumericEntries(block, start);
	if (numbered.length >= 2) return numbered;
	const entries = splitReferenceBlock(block);
	const out: ReferenceEntry[] = [];

	for (const entry of entries) {
		const parsed = parseReferenceEntry(entry.text);
		if (!parsed) continue;
		out.push({
			id: `ref-${out.length + 1}`,
			raw: entry.text.replace(/\s+/g, ' ').trim(),
			authors: parsed.authors,
			year: parsed.year,
			title: parsed.title,
			key: makeKey(parsed.authors, parsed.year),
			location: { charStart: start + entry.offset, charEnd: start + entry.offset + entry.text.length },
			confidence: parsed.confidence
		});
	}
	return out;
}

/** [1] Author... / 1. Author... biçimindeki numaralı kaynakça girişleri. */
function extractNumericEntries(block: string, base: number): ReferenceEntry[] {
	// Yalnızca gerçekten numaralı kaynakça: [1] biçimi ya da 1,2,3… sırası.
	const prefixes = [...block.matchAll(/(?:^|\n)\s*\[?(\d{1,3})[\]\.\)]\s+/g)].map((m) =>
		Number(m[1])
	);
	const bracketed = /(?:^|\n)\s*\[\d{1,3}\]/m.test(block);
	const sequential =
		prefixes.length >= 3 &&
		prefixes[0] === 1 &&
		prefixes.every((n, i) => n === i + 1);
	if (!bracketed && !sequential) return [];

	const re = /(?:^|\n)\s*\[?(\d{1,3})[\]\.\)]\s+([\s\S]*?)(?=\n\s*\[?\d{1,3}[\]\.\)]\s+|$)/g;
	const out: ReferenceEntry[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(block))) {
		const number = Number(m[1]);
		const raw = m[2].replace(/\s+/g, ' ').trim();
		if (!Number.isFinite(number) || raw.length < 12) continue;
		const parsed = parseReferenceEntry(raw);
		const year =
			parsed?.year ??
			Number(/(?:^|[^\d])((?:19|20)\d{2})(?:[^\d]|$)/.exec(raw)?.[1] ?? Number.NaN);
		const authors = parsed?.authors?.length
			? parsed.authors
			: raw
					.split(/[.,(]/)
					.slice(0, 2)
					.map((s) => s.trim())
					.filter(Boolean);
		out.push({
			id: `ref-${out.length + 1}`,
			raw,
			authors,
			year: Number.isFinite(year) ? year : undefined,
			number,
			title: parsed?.title,
			key: `#${number}`,
			location: { charStart: base + m.index, charEnd: base + m.index + m[0].length },
			confidence: 'high'
		});
	}
	return out;
}

/** Kaynakça bloğunu girdilere böler: yıl ve satır başı ipuçlarını kullanır. */
function splitReferenceBlock(block: string): { text: string; offset: number }[] {
	const lines = block.split('\n');
	const entries: { text: string; offset: number }[] = [];
	let current: { text: string; offset: number } | null = null;
	let cursor = 0;

	// Girdi başlangıcı: yıl parantezi veya satır başında soyadı başlıyor
	const startRe = new RegExp(
		String.raw`\(\s*${YEAR_ALT}\s*\)|^\s*[A-ZÇĞİÖŞÜ][\p{L}'’\-]+,\s*[A-ZÇĞİÖŞÜ]`,
		'u'
	);

	for (const line of lines) {
		const lineNo = cursor;
		cursor += line.length + 1;
		const trimmed = line.trim();
		if (!trimmed) {
			if (current) {
				entries.push(current);
				current = null;
			}
			continue;
		}

		const isNew = startRe.test(trimmed) && trimmed.length > 12;
		if (isNew || !current) {
			if (current) entries.push(current);
			current = { text: trimmed, offset: lineNo + (line.length - line.trimStart().length) };
		} else {
			current.text += ' ' + trimmed;
		}
	}
	if (current) entries.push(current);

	// Aşırı uzun "girdiler" büyük olasılıkla birleşmiştir; böl
	const result: { text: string; offset: number }[] = [];
	for (const e of entries) {
		if (e.text.length > 900) {
			const parts = splitLongEntry(e);
			result.push(...parts);
		} else {
			result.push(e);
		}
	}
	return result.filter((e) => e.text.length >= 15);
}

function splitLongEntry(e: { text: string; offset: number }): { text: string; offset: number }[] {
	const re = new RegExp(String.raw`(?=\s[A-ZÇĞİÖŞÜ][\p{L}'’\-]+,\s*[A-ZÇĞİÖŞÜ]\.)`, 'gu');
	const parts = e.text.split(re);
	if (parts.length <= 1) return [e];
	const out: { text: string; offset: number }[] = [];
	let cursor = 0;
	for (const p of parts) {
		if (!p.trim()) continue;
		const idx = e.text.indexOf(p, cursor);
		out.push({ text: p.trim(), offset: e.offset + (idx === -1 ? cursor : idx) });
		cursor = (idx === -1 ? cursor : idx) + p.length;
	}
	return out;
}

interface ParsedReference {
	authors: string[];
	year?: number;
	title?: string;
	confidence: Confidence;
}

function parseReferenceEntry(raw: string): ParsedReference | null {
	const text = raw.replace(/\s+/g, ' ').trim();
	if (text.length < 12) return null;

	// APA: "Yılmaz, A., & Demir, B. (2019). Başlık. Dergi, 12(3), 45-67."
	const apa = /^(.+?)\s*\(\s*((?:\d{4}[a-z]?)|n\.?\s?d\.?|baskıda|in\s+press)\s*\)\s*[.,]?\s*(.*)$/i.exec(
		text
	);
	// Türkçe tez: "Yılmaz, A. (2019). Başlık. Yayımlanmamış yüksek lisans tezi."
	let authorPart: string | undefined;
	let yearStr: string | undefined;
	let rest: string | undefined;

	if (apa) {
		authorPart = apa[1];
		yearStr = apa[2];
		rest = apa[3];
	} else {
		// Chicago/MLA benzeri: "Yılmaz, Ahmet. 2019. Başlık."
		const alt = /^(.+?)[.,]\s*(\d{4})[a-z]?\s*[.,]\s*(.*)$/.exec(text);
		if (alt) {
			authorPart = alt[1];
			yearStr = alt[2];
			rest = alt[3];
		}
	}

	if (!authorPart || !yearStr) {
		// Yıl yok: yine de yazar çıkar, düşük güven
		const fallbackAuthors = extractAuthors(text.slice(0, 80));
		if (fallbackAuthors.length === 0) return null;
		return {
			authors: fallbackAuthors,
			title: excerpt(text, 120),
			confidence: 'low'
		};
	}

	const authors = extractAuthors(authorPart);
	if (authors.length === 0) return null;

	const year = /^\d{4}/.test(yearStr) ? Number(yearStr.slice(0, 4)) : undefined;
	const title = extractTitle(rest ?? '', yearStr);

	let confidence: Confidence = 'high';
	if (!/\(\s*\d{4}/.test(raw) && !/^\d{4}$/.test(yearStr)) confidence = 'medium';
	if (authors.length === 0) confidence = 'low';

	return { authors, year, title, confidence };
}

function extractAuthors(authorPart: string): string[] {
	let s = authorPart.trim().replace(/[.,;]+$/, '');
	if (!s) return [];

	// "Yılmaz, A., & Demir, B." → ["Yılmaz", "Demir"]
	// "Yılmaz, A. ve Demir, B."
	const parts = s.split(/\s*(?:,|&|\bve\b|\band\b)\s*/i);
	const authors: string[] = [];
	for (const part of parts) {
		const cleaned = normalizeAuthorName(part);
		if (!cleaned) continue;
		if (/^\d/.test(cleaned)) continue;
		// Tek harfli baş harf parçalarını at: "S", "R."
		if (/^[A-ZÇĞİÖŞÜ]\.?$/.test(cleaned)) continue;
		if (cleaned.length > 40) continue;
		authors.push(cleaned);
	}
	return authors.slice(0, 12);
}

function extractTitle(rest: string, yearStr: string): string | undefined {
	let s = rest.replace(/^[.,]\s*/, '').trim();
	if (!s) return undefined;
	// Yıl tekrarı: "2019. Başlık"
	s = s.replace(new RegExp(String.raw`^${yearStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\s*[.,]\s*`), '');
	// İlk cümle başlık kabul edilir
	const m = /^(.{5,200}?)(?:\.\s|$)/.exec(s);
	return m ? m[1].trim() : excerpt(s, 160);
}

function makeKey(authors: string[], year?: number): string {
	const first = authors[0] ?? 'anon';
	return `${foldTurkish(first).toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')}${year ?? ''}`;
}

/* -------------------------------------------------------------------------- */
/* Eşleştirme                                                                  */
/* -------------------------------------------------------------------------- */

function matchCitations(
	citations: Citation[],
	references: ReferenceEntry[]
): { matches: CitationMatch[]; orphans: ReferenceEntry[] } {
	const matches: CitationMatch[] = [];
	const used = new Set<string>();

	for (const cit of citations) {
		if (cit.number !== undefined) {
			const ref = references.find((r) => r.number === cit.number);
			if (ref) {
				used.add(ref.id);
				matches.push({ citation: cit, matched: ref, status: 'matched', candidates: [ref] });
			} else {
				matches.push({ citation: cit, status: 'missing', candidates: [] });
			}
			continue;
		}
		const authorKey = cit.authors[0] ? foldTurkish(cit.authors[0]).toLowerCase() : '';
		const candidates = references.filter((ref) => {
			if (!ref.authors[0]) return false;
			const refKey = foldTurkish(ref.authors[0]).toLowerCase();
			return similarity(refKey, authorKey) >= 0.86;
		});

		if (candidates.length === 0) {
			matches.push({ citation: cit, status: 'missing', candidates: [] });
			continue;
		}

		const exactYear = candidates.find((c) => c.year === cit.year);
		if (exactYear) {
			used.add(exactYear.id);
			matches.push({ citation: cit, matched: exactYear, status: 'matched', candidates });
			continue;
		}

		// Yıl yoksa (n.d.) yazar eşleşmesi yeterli
		if (cit.year === undefined && candidates.length > 0) {
			const c = candidates[0];
			used.add(c.id);
			matches.push({ citation: cit, matched: c, status: 'matched', candidates });
			continue;
		}

		// Yıl farklı ama yazar yakın → year-mismatch; yazar da uzaksa author-mismatch
		const best = candidates[0];
		used.add(best.id);
		matches.push({
			citation: cit,
			matched: best,
			status: 'year-mismatch',
			candidates
		});
	}

	// Yazar uyuşmazlığı: aynı yıl var, yazar benzer değil
	for (const m of matches) {
		if (m.status !== 'missing') continue;
		const sameYear = references.find((r) => r.year === m.citation.year);
		if (!sameYear) continue;
		m.status = 'author-mismatch';
		m.matched = sameYear;
		m.candidates = [sameYear];
		used.add(sameYear.id);
	}

	const usedIds = new Set(matches.map((m) => m.matched?.id).filter(Boolean) as string[]);
	return {
		matches,
		orphans: references.filter((r) => !usedIds.has(r.id) && !isNoiseEntry(r))
	};
}

/** Teşekkür, editör notu gibi kaynakça dışı girdileri ele. */
function isNoiseEntry(r: ReferenceEntry): boolean {
	const raw = r.raw.toLowerCase();
	return /^(?:teşekkür|thanks|acknowledg|not:|editör|editor)/i.test(raw) || raw.length < 15;
}

/** Jaro-Winkler benzerliği (0-1). */
export function similarity(a: string, b: string): number {
	if (a === b) return 1;
	if (!a || !b) return 0;
	const maxLen = Math.max(a.length, b.length);
	const matchWindow = Math.max(0, Math.floor(maxLen / 2) - 1);
	const aMatched = new Array<boolean>(a.length).fill(false);
	const bMatched = new Array<boolean>(b.length).fill(false);

	let matches = 0;
	for (let i = 0; i < a.length; i++) {
		const start = Math.max(0, i - matchWindow);
		const end = Math.min(i + matchWindow + 1, b.length);
		for (let j = start; j < end; j++) {
			if (bMatched[j] || a[i] !== b[j]) continue;
			aMatched[i] = true;
			bMatched[j] = true;
			matches++;
			break;
		}
	}
	if (matches === 0) return 0;

	let transpositions = 0;
	let k = 0;
	for (let i = 0; i < a.length; i++) {
		if (!aMatched[i]) continue;
		while (!bMatched[k]) k++;
		if (a[i] !== b[k]) transpositions++;
		k++;
	}
	const t = transpositions / 2;
	const jaro = (matches / a.length + matches / b.length + (matches - t) / matches) / 3;

	// Winkler: ortak önek (max 4)
	let prefix = 0;
	for (let i = 0; i < Math.min(4, a.length, b.length); i++) {
		if (a[i] !== b[i]) break;
		prefix++;
	}
	return jaro + prefix * 0.1 * (1 - jaro);
}

function clamp(v: number): number {
	return Math.max(0, Math.min(100, v));
}
